import { type GrpcClient } from '@pushwoosh/http-client';
import { FiltersServiceClient } from '@pushwoosh/rpc-service-client/pushwoosh/filters/FiltersServiceClientPb';
import {
  Filter,
  GetFilterRequest,
  ListFiltersRequest,
  CreateFilterRequest,
  UpdateFilterRequest,
  DeleteFilterRequest,
  CompileFilterRequest,
  CloneFilterRequest,
} from '@pushwoosh/rpc-service-client/pushwoosh/filters/filters_pb';
import type {
  SegmentData,
  SegmentDataCompilingCalculationStatus,
} from '../../types';
import { SegmentCondition } from './types';
import { parseFilterExpression } from './parseFilterExpression';
import { toFilterExpression } from './toFilterExpression';

type SegmentListParams = {
  applicationCode?: string;
  name?: string;
  page?: number;
  perPage?: number;
  highSpeedOnly?: boolean;
  segmentCodes?: string[];
  orderBy?: 'name' | 'created';
  orderDirection?: 'asc' | 'desc';
};

type SegmentGetResult = {
  segments: SegmentData[];
  page: number;
  perPage: number;
  totalPages: number;
};

type SegmentGetParams = {
  applicationCode?: string;
  code: string;
};

type SegmentCreateParams = {
  name: string;
  applicationCode: string;
  condition: SegmentCondition;
  highSpeedApplication?: string;
};

type SegmentUpdateParams = {
  code: string;
  name: string;
  applicationCode: string;
  condition: SegmentCondition;
};

type CompileParams = {
  applicationCode: string;
  code: string;
  exportCsv: boolean;
};

export type SegmentsGrpcBridge = {
  list: (params: SegmentListParams) => Promise<SegmentGetResult>;
  get: (params: SegmentGetParams) => Promise<SegmentData>;
  create: (params: SegmentCreateParams) => Promise<{ code: string }>;
  update: (params: SegmentUpdateParams) => Promise<void>;
  delete: (params: { code: string }) => Promise<void>;
  compile: (params: CompileParams) => Promise<void>;
  clone: (params: { code: string; name: string }) => Promise<{ code: string }>;
};

function filter2segment(filter: Filter): SegmentData {
  const segment: SegmentData = {
    name: filter.getName(),
    code: filter.getCode(),
    highspeedReady: filter.getHighspeedReady(),
    highSpeedApplication: filter.getHighspeedApplication(),
    hasRightAceModify: filter.getHasRightAceModify(),
    condition: parseFilterExpression(filter.getFilterExpression()!),
  };

  if (filter.hasCompiling()) {
    const comp = filter.getCompiling()!;
    segment.compiling = {
      size: comp.getSize(),
      reachable: comp.getReachable(),
      calculationStatus:
        comp.getCalculationStatus() as SegmentDataCompilingCalculationStatus,
      calculationProgress: comp.getCalculationProgress(),
      csvPath: comp.getCsvPath(),
      calculationDate: comp.getCalculationDate()?.getSeconds(),
    };
  }

  return segment;
}

export function createSegmentsGrpcBridge(
  grpcClient: GrpcClient,
): SegmentsGrpcBridge {
  const client = grpcClient.createServiceClient(FiltersServiceClient);
  return {
    list: async (params) => {
      const req = new ListFiltersRequest();
      if (params.applicationCode) {
        req.setApplication(params.applicationCode);
      }
      if (params.name) {
        req.setSearchByName(params.name);
      }
      if (params.page) {
        req.setPage(params.page);
      }
      if (params.perPage) {
        req.setPerPage(params.perPage);
      }
      if (typeof params.highSpeedOnly === 'boolean') {
        req.setHighSpeedOnly(params.highSpeedOnly);
      }
      if (params.segmentCodes) {
        req.setCodesList(params.segmentCodes);
      }
      if (params.orderBy) {
        req.setOrderBy(
          params.orderBy === 'name'
            ? ListFiltersRequest.OrderBy.NAME
            : ListFiltersRequest.OrderBy.CREATED,
        );
      }
      if (params.orderDirection) {
        req.setOrderDirection(
          params.orderDirection === 'asc'
            ? ListFiltersRequest.OrderDirection.ASC
            : ListFiltersRequest.OrderDirection.DESC,
        );
      }
      const resp = await client.list(req, null);
      return {
        segments: resp.getFiltersList().map(filter2segment),
        page: resp.getPage(),
        perPage: resp.getPerPage(),
        totalPages: resp.getTotalPages(),
      };
    },
    get: async (params) => {
      const req = new GetFilterRequest().setFilter(params.code);
      if (params.applicationCode) {
        req.setApplication(params.applicationCode);
      }
      const resp = await client.get(req, null);

      return filter2segment(resp);
    },
    create: async (params) => {
      const filterExpression = toFilterExpression(
        params.applicationCode,
        params.condition,
      );
      const request = new CreateFilterRequest()
        .setName(params.name)
        .setFilterExpression(filterExpression);
      if (params.highSpeedApplication) {
        request.setHighspeedApplication(params.highSpeedApplication);
      }
      const response = await client.create(request, null);
      return {
        code: response.getFilter(),
      };
    },
    update: async (params) => {
      const filterExpression = toFilterExpression(
        params.applicationCode,
        params.condition,
      );
      const request = new UpdateFilterRequest()
        .setFilter(params.code)
        .setName(params.name)
        .setFilterExpression(filterExpression);
      await client.update(request, null);
    },
    delete: async (params) => {
      const request = new DeleteFilterRequest().setFilter(params.code);
      await client.delete(request, null);
    },
    compile: async (params) => {
      const request = new CompileFilterRequest()
        .setApplication(params.applicationCode)
        .setFilter(params.code)
        .setExportCsv(params.exportCsv);
      await client.compileFilter(request, null);
    },
    clone: async (params) => {
      const request = new CloneFilterRequest()
        .setFilter(params.code)
        .setNewName(params.name);
      const response = await client.clone(request, null);
      return {
        code: response.getFilter(),
      };
    },
  };
}
